/**
 * Report Settings Page
 *
 * Lets workspace admins control which reports appear on the Reports page
 * and which are required (pinned, non-hideable by members).
 * Also manages session result report templates.
 */

import { useEffect, useState } from 'react';
import { BarChart3, Eye, EyeOff, Pin, FileText, Sparkles, Lock } from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { getCurrentWorkspaceId } from '../../../lib/supabaseSync';
import { REPORT_AREAS, REPORT_REGISTRY, effectiveReportAreas, type ReportDefinition } from '../../../lib/reportSettingsService';
import { AREA_LABEL, type AppArea } from '../../../lib/appAreas';
import { useReportSettingsStore } from '../../../stores/prioritization/reportSettingsStore';
import {
  fetchReportTemplates,
  SYSTEM_REPORT_TEMPLATES,
} from '../../../lib/reportGenerationService';
import type { ReportTemplate } from '../../../lib/reportGenerationService';

// ── Report Template Row ───────────────────────────────────────────────────────

const SECTION_TYPE_LABELS: Record<string, string> = {
  headline: 'Headline',
  ranked_list: 'Ranked List',
  ai_narrative: 'AI Narrative',
  relationship_notes: 'Relationships',
  session_metadata: 'Metadata',
  context_header: 'Context Header',
  text: 'Free Text',
  contact: 'Contact',
};

function TemplateSectionBadge({ type }: { type: string }) {
  const isAi = type === 'ai_narrative';
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${
      isAi ? 'bg-[var(--slate-dim)] text-[var(--text-sec)]' : 'bg-[var(--hover-tint)] text-[var(--text-sec)]'
    }`}>
      {isAi && <Sparkles className="h-3 w-3" />}
      {SECTION_TYPE_LABELS[type] ?? type}
    </span>
  );
}

function ReportTemplateCard({ template }: { template: ReportTemplate }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-8 h-8 bg-[var(--surface)] border border-[var(--rule)] flex items-center justify-center">
          <FileText className="h-4 w-4 text-[var(--text-ter)]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-[var(--text-pri)]">{template.name}</span>
            {template.isSystem && (
              <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-full font-medium bg-[var(--slate-dim)] text-[var(--text-sec)]">
                <Lock className="h-3 w-3" />
                System
              </span>
            )}
            {template.audience && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-[var(--hover-tint)] text-[var(--text-ter)] capitalize">
                {template.audience}
              </span>
            )}
          </div>
          {template.description && (
            <p className="text-sm text-[var(--text-ter)] mt-0.5">{template.description}</p>
          )}
          <button
            onClick={() => setExpanded(v => !v)}
            className="mt-2 text-xs text-primary-600 hover:text-primary-700 font-medium"
          >
            {expanded ? 'Hide sections' : `View ${template.sections.length} sections`}
          </button>
          {expanded && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {template.sections.map(s => (
                <TemplateSectionBadge key={s.id} type={s.type} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Report Templates Section ──────────────────────────────────────────────────

function ReportTemplatesSection({ workspaceId }: { workspaceId: string | null }) {
  const [templates, setTemplates] = useState<ReportTemplate[]>(SYSTEM_REPORT_TEMPLATES);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!workspaceId) return;
    setIsLoading(true);
    fetchReportTemplates(workspaceId)
      .then(setTemplates)
      .catch(() => setTemplates(SYSTEM_REPORT_TEMPLATES))
      .finally(() => setIsLoading(false));
  }, [workspaceId]);

  const systemTemplates = templates.filter(t => t.isSystem);
  const customTemplates = templates.filter(t => !t.isSystem);

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-[var(--text-pri)]">Session Report Templates</h3>
        <p className="text-xs text-[var(--text-ter)] mt-0.5">
          Templates used to generate shareable reports from completed sessions.
        </p>
      </div>

      {isLoading ? (
        <div className="card text-sm text-[var(--text-ter)] py-6 text-center">Loading templates…</div>
      ) : (
        <>
          <div>
            <p className="text-xs font-medium text-[var(--text-ter)] mb-2 uppercase tracking-wide">System templates</p>
            <div className="space-y-2">
              {systemTemplates.map(t => (
                <ReportTemplateCard key={t.id} template={t} />
              ))}
            </div>
          </div>

          {customTemplates.length > 0 && (
            <div>
              <p className="text-xs font-medium text-[var(--text-ter)] mb-2 uppercase tracking-wide">Custom templates</p>
              <div className="space-y-2">
                {customTemplates.map(t => (
                  <ReportTemplateCard key={t.id} template={t} />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Legacy section labels ─────────────────────────────────────────────────────

const SECTION_LABELS: Record<string, string> = {
  analysis: 'Analysis Reports',
  canonical: 'Canonical Reporting Surfaces',
  overview: 'Overview Reports',
};

const SECTION_ORDER = ['analysis', 'canonical', 'overview'] as const;

// ── Report Row ────────────────────────────────────────────────────────────────

interface ReportRowProps {
  report: ReportDefinition;
  isVisible: boolean;
  isRequired: boolean;
  onToggleVisible: () => void;
  onToggleRequired: () => void;
  /** This workspace's placement; null = the platform's. */
  areas: AppArea[] | null;
  onSetAreas: (areas: AppArea[] | null) => void;
}

/** Which areas' Reports menus show this report — the platform's placement, or this workspace's. */
function AreaPlacement({ report, areas, onSetAreas }: Pick<ReportRowProps, 'report' | 'areas' | 'onSetAreas'>) {
  if (report.surface !== 'home') {
    return (
      <p className="mt-2 text-xs text-[var(--text-ter)]">
        Not in a menu — reached from the page it reports on.
      </p>
    );
  }
  const current = effectiveReportAreas(report, { areas });
  const changed = areas !== null;
  const toggle = (area: AppArea) => {
    const next = current.includes(area) ? current.filter((a) => a !== area) : [...current, area];
    // Keep the area order of the switch.
    onSetAreas(REPORT_AREAS.filter((a) => next.includes(a)));
  };
  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid={`report-areas-${report.key}`}>
      <span className="text-xs text-[var(--text-ter)] mr-1">Appears in:</span>
      {REPORT_AREAS.map((area) => {
        const on = current.includes(area);
        return (
          <button
            key={area}
            type="button"
            role="switch"
            aria-checked={on}
            aria-label={`${AREA_LABEL[area]} Reports menu`}
            onClick={() => toggle(area)}
            className={`px-2 py-0.5 text-xs border transition-colors ${
              on
                ? 'border-[var(--slate)] bg-[var(--slate-dim)] text-[var(--text-pri)] font-medium'
                : 'border-[var(--rule)] text-[var(--text-ter)] hover:bg-[var(--hover-tint)]'
            }`}
          >
            {AREA_LABEL[area]}
          </button>
        );
      })}
      {current.length === 0 && <span className="text-xs text-[var(--mustard)]">in no menu — only on All Reports</span>}
      {changed ? (
        <button
          type="button"
          onClick={() => onSetAreas(null)}
          className="ml-1 text-xs text-[var(--text-ter)] underline hover:text-[var(--text-sec)]"
          title={`Platform placement: ${report.areas.map((a) => AREA_LABEL[a]).join(', ') || 'none'}`}
        >
          Changed for this workspace · use the platform&apos;s
        </button>
      ) : (
        <span className="ml-1 text-xs text-[var(--text-ter)]">Platform placement</span>
      )}
    </div>
  );
}

function ReportRow({ report, isVisible, isRequired, onToggleVisible, onToggleRequired, areas, onSetAreas }: ReportRowProps) {
  return (
    <div className={`flex items-start gap-4 p-4  border transition-colors ${
      isVisible ? 'bg-[var(--bg-raised)] border-[var(--rule)]' : 'bg-[var(--surface)] border-[var(--rule)]'
    }`}>
      {/* Visibility toggle */}
      <div className="flex-shrink-0 mt-0.5">
        <button
          onClick={onToggleVisible}
          disabled={isRequired}
          title={isRequired ? 'Required reports cannot be hidden' : isVisible ? 'Hide from reports page' : 'Show on reports page'}
          className={`p-1.5  transition-colors ${
            isRequired
              ? 'text-[var(--text-ter)] cursor-not-allowed'
              : isVisible
              ? 'text-[var(--green)] dark:text-[var(--text-ter)] hover:bg-[var(--green-dim)]'
              : 'text-[var(--text-ter)] hover:bg-[var(--hover-tint)]'
          }`}
        >
          {isVisible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-sm font-semibold ${isVisible ? 'text-[var(--text-pri)]' : 'text-[var(--text-ter)]'}`}>
            {report.name}
          </span>
          {isRequired && (
            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium bg-[var(--slate-dim)] text-[var(--text-sec)]">
              <Pin className="w-3 h-3" />
              Required
            </span>
          )}
          {!isVisible && !isRequired && (
            <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-[var(--hover-tint)] text-[var(--text-ter)]">
              Hidden
            </span>
          )}
        </div>
        <p className={`text-sm mt-0.5 ${isVisible ? 'text-[var(--text-ter)]' : 'text-[var(--text-ter)]'}`}>
          {report.description}
        </p>
        <AreaPlacement report={report} areas={areas} onSetAreas={onSetAreas} />
      </div>

      {/* Required toggle */}
      <div className="flex-shrink-0 mt-0.5">
        <button
          onClick={onToggleRequired}
          title={isRequired ? 'Unmark as required' : 'Mark as required — always shown, cannot be hidden'}
          className={`p-1.5  transition-colors ${
            isRequired
              ? 'text-[var(--text-sec)] bg-[var(--slate-dim)] hover:bg-[var(--slate-dim)]'
              : 'text-[var(--text-ter)] hover:text-[var(--text-ter)] hover:bg-[var(--hover-tint)]'
          }`}
        >
          <Pin className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function ReportSettings() {
  const workspaceId = getCurrentWorkspaceId();
  const { fetchSettings, getSetting, toggleVisible, toggleRequired, setAreas, settings, loading, error } =
    useReportSettingsStore();

  useEffect(() => {
    if (workspaceId) fetchSettings(workspaceId);
  }, [workspaceId, fetchSettings]);

  const reportsBySection = SECTION_ORDER.map((section) => ({
    section,
    label: SECTION_LABELS[section],
    reports: REPORT_REGISTRY.filter((r) => r.section === section),
  }));

  return (
    <div className="space-y-8">
      <PageHeader
        icon={<BarChart3 className="h-4 w-4 text-[var(--text-sec)]" />}
        iconBg="bg-[var(--hover-tint)]"
        title="Report Settings"
        subtitle="Control which reports appear on the Reports page and in each area's Reports menu, and manage session result report templates."
      />

      {!workspaceId && (
        <div className="card bg-[var(--mustard-dim)] border-[var(--mustard)]">
          <p className="text-sm text-[var(--mustard)]">Select a workspace in the sidebar to manage its report settings.</p>
        </div>
      )}

      {error && (
        <div className="card bg-[var(--coral-dim)] border-[var(--coral)]">
          <p className="text-sm text-[var(--coral)]">{error}</p>
        </div>
      )}

      {/* Legend */}
      <div className="card bg-[var(--slate-dim)] border-[var(--slate)] space-y-2">
        <p className="text-sm font-medium text-[var(--text-sec)]">How this works</p>
        <div className="flex flex-col gap-1.5 text-sm text-[var(--text-sec)]">
          <div className="flex items-center gap-2">
            <Eye className="w-4 h-4 flex-shrink-0" />
            <span><strong>Visible</strong> — shown on the Reports page by default. Click the eye to hide it.</span>
          </div>
          <div className="flex items-center gap-2">
            <Pin className="w-4 h-4 flex-shrink-0" />
            <span><strong>Required</strong> — always shown, cannot be hidden. Displayed with a "Required" badge on the Reports page.</span>
          </div>
          <div className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4 flex-shrink-0" />
            <span><strong>Appears in</strong> — which areas&apos; Reports menus in the left nav list the report: Strategy, Priorities, Plans, Action. A report can be in more than one. Every report is always on All Reports.</span>
          </div>
        </div>
      </div>

      {workspaceId && (
        <div className="space-y-8">
          {/* Session Report Templates */}
          <div className="card p-5">
            <ReportTemplatesSection workspaceId={workspaceId} />
          </div>

          {/* Reports Page Visibility */}
          <div>
            <h3 className="text-sm font-semibold text-[var(--text-sec)] mb-1">Reports Page Visibility</h3>
            <p className="text-xs text-[var(--text-ter)] mb-3">Control which reports appear on the Reports page for your workspace.</p>

            {loading ? (
              <div className="card text-sm text-[var(--text-ter)] py-6 text-center">Loading report settings…</div>
            ) : (
              reportsBySection.map(({ section, label, reports }) => (
                <div key={section} className="mb-6">
                  <h4 className="text-sm font-semibold text-[var(--text-sec)] mb-3">{label}</h4>
                  <div className="card space-y-2 p-3">
                    {reports.map((report) => {
                      const { isVisible, isRequired } = getSetting(report.key);
                      return (
                        <ReportRow
                          key={report.key}
                          report={report}
                          isVisible={isVisible}
                          isRequired={isRequired}
                          onToggleVisible={() => toggleVisible(workspaceId, report.key)}
                          onToggleRequired={() => toggleRequired(workspaceId, report.key)}
                          areas={settings.find((s) => s.reportKey === report.key)?.areas ?? null}
                          onSetAreas={(next) => void setAreas(workspaceId, report.key, next)}
                        />
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
